import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject, Observable, interval, Subscription, Subject } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { BaseApiService } from 'src/app/shared/services';
import { backgroundContext } from 'src/app/core/interceptor/background-request.context';

import { TaskStatus } from '../models/task-status.model';

@Injectable({
  providedIn: 'root'
})
export class TaskStatusService extends BaseApiService<TaskStatus> {
  // Singleton instance
  private static instance: TaskStatusService;

  private pollingInterval = 5000; // 5 seconds
  private pollingSubscription: Subscription | null = null;
  private isPollingActive = false;
  public autoStopPolling = true;

  private oldTasks: TaskStatus[] = [];
  private tasksSubject = new BehaviorSubject<TaskStatus[]>([]);
  public tasks$ = this.tasksSubject.asObservable();

  private noReadTasksSubject = new BehaviorSubject<TaskStatus[]>([]);
  public noReadTasks$ = this.noReadTasksSubject.asObservable();

  private pendingTasksCountSubject = new BehaviorSubject<number>(0);
  public pendingTasksCount$ = this.pendingTasksCountSubject.asObservable();

  // Emite eventos de tarefas concluídas
  private taskCompletedSubject = new Subject<{taskId: string, taskType: string, result: any}>();
  public taskCompleted$ = this.taskCompletedSubject.asObservable();

  constructor(protected injector: Injector) {
    super('/api/core/task-status/', injector);

    // Ensure singleton pattern
    if (TaskStatusService.instance) {
      return TaskStatusService.instance;
    }

    // Set the singleton instance
    TaskStatusService.instance = this;

    // Initial fetch without starting polling
    this.fetchTasks().subscribe();
  }

  /**
   * Starts polling for task updates
   * @returns boolean indicating if polling was started (true) or was already active (false)
   */
  public startPolling(): boolean {
    if (this.isPollingActive) {
      return false;
    }

    this.isPollingActive = true;
    this.pollingSubscription = interval(this.pollingInterval)
      .pipe(
        switchMap(() => this.fetchTasks())
      )
      .subscribe(tasks => {
        // If no pending tasks, stop polling automatically
        if (this.autoStopPolling && !this.hasPendingTasks(tasks)) {
          this.stopPolling();
        }
      });

    return true;
  }

  // Método para verificar tarefas concluídas
  private checkForCompletedTasks(currentTasks: TaskStatus[]): void {
    // Encontrar tarefas que mudaram de PENDING/PROCESSING para COMPLETED
    currentTasks.forEach(currentTask => {
      const previousTask = this.oldTasks.find(task => task.id === currentTask.id);

      // Verificar se a tarefa existia anteriormente e mudou de status para COMPLETED
      if (previousTask &&
          (previousTask.status === 'PENDING' || previousTask.status === 'PROCESSING') &&
          currentTask.status === 'COMPLETED') {

        // Emitir evento de tarefa concluída
        this.taskCompletedSubject.next({
          taskId: currentTask.task_id,
          taskType: currentTask.task_type,
          result: currentTask.result
        });
      }
    });

    // Atualizar oldTasks após a verificação
    this.oldTasks = [...currentTasks];
  }


  /**
   * Stops polling for task updates
   * @returns boolean indicating if polling was stopped (true) or was already inactive (false)
   */
  public stopPolling(): boolean {
    if (!this.isPollingActive) {
      return false;
    }

    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = null;
    }

    this.isPollingActive = false;
    return true;
  }

  /**
   * Checks if there are any pending tasks
   */
  private hasPendingTasks(tasks: TaskStatus[]): boolean {
    return tasks.some(task =>
      task.status === 'PENDING' || task.status === 'PROCESSING'
    );
  }

  /**
   * Notifies the service that a new task was sent to the server
   * This will trigger polling if it's not already active
   */
  public notifyNewTaskSent(): void {
    // Start polling and immediately fetch tasks
    this.startPolling();
    this.fetchTasks().subscribe();
  }

  /**
   * @returns boolean indicating if polling is currently active
   */
  public isPollingEnabled(): boolean {
    return this.isPollingActive;
  }

  private fetchTasks(): Observable<TaskStatus[]> {
    // Polling de status não conta como atividade do usuário.
    return this.find({}, '', backgroundContext()).pipe(
      tap(tasks => {
        this.tasksSubject.next(tasks);
        const noReadTasks = tasks.filter(task => !task.read);
        this.noReadTasksSubject.next(noReadTasks);
        this.updatePendingTasksCount(noReadTasks);

        // Verificar tarefas que foram concluídas nesta atualização
        this.checkForCompletedTasks(tasks);
      })
    );
  }

  private updatePendingTasksCount(noReadTasks: TaskStatus[]): void {
    // Badge soma TODAS as tasks não-lidas (status pendente/processando,
    // concluído ou notificações). Usuário marca como lida no dropdown
    // ou via "Marca todas como lidas".
    this.pendingTasksCountSubject.next(noReadTasks.length);
  }

  public getTaskById(taskId: string): Observable<TaskStatus> {
    return this.http.get<TaskStatus>(`${this.apiPath}${taskId}/`);
  }

  public markTaskAsRead(taskId: string): Observable<void> {
    return this.http.patch<void>(`${this.apiPath}${taskId}/mark-as-read/`, {});
  }

  public markAllAsRead(): Observable<void> {
    return this.http.patch<void>(`${this.apiPath}mark-all-as-read/`, {});
  }
}
